import type { FocalPoint } from '@/shared/image/cropGeometry'
import type { SocialResizePresetId } from './presets'

export interface SocialResizeWorkerRequest {
  generation: number
  jobId: string
  file: File
  presetId: SocialResizePresetId
  focal: FocalPoint
  mime: 'image/jpeg' | 'image/png'
  quality: number
}

export type SocialResizeWorkerResponse =
  | { ok: true; generation: number; jobId: string; blob: Blob }
  | {
    ok: false
    generation: number
    jobId: string
    code: 'decode_failed' | 'canvas_failed' | 'encode_failed' | 'invalid_request' | 'timeout'
  }

interface WorkerLike {
  onmessage: ((event: MessageEvent<SocialResizeWorkerResponse>) => void) | null
  onerror: ((event: ErrorEvent) => unknown) | null
  postMessage: (request: SocialResizeWorkerRequest) => void
  terminate: () => void
}

export interface SocialResizeWorkerOwner {
  promise: Promise<SocialResizeWorkerResponse>
  cancel: () => void
}

export const SOCIAL_RESIZE_WORKER_TIMEOUT_MS = 12_000

interface SocialResizeWorkerDependencies {
  createWorker?: () => WorkerLike
  setTimer?: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>
  clearTimer?: (timer: ReturnType<typeof setTimeout>) => void
}

export function runSocialResizeWorker(
  request: SocialResizeWorkerRequest,
  dependencies: SocialResizeWorkerDependencies = {},
): SocialResizeWorkerOwner {
  const setTimer = dependencies.setTimer ?? setTimeout
  const clearTimer = dependencies.clearTimer ?? clearTimeout
  let worker: WorkerLike | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let settled = false
  let resolveResponse: (response: SocialResizeWorkerResponse) => void = () => undefined
  const promise = new Promise<SocialResizeWorkerResponse>((resolve) => {
    resolveResponse = resolve
  })
  const settle = (response: SocialResizeWorkerResponse) => {
    if (settled) return
    settled = true
    if (worker) {
      worker.onmessage = null
      worker.onerror = null
      worker.terminate()
      worker = null
    }
    if (timer !== null) {
      clearTimer(timer)
      timer = null
    }
    resolveResponse(response)
  }
  try {
    worker = dependencies.createWorker?.() ?? new Worker(
      new URL('../worker/socialResize.worker.ts', import.meta.url),
      { type: 'module' },
    )
    worker.onmessage = (event) => settle(event.data)
    worker.onerror = () => settle({
      ok: false,
      generation: request.generation,
      jobId: request.jobId,
      code: 'canvas_failed',
    })
    worker.postMessage(request)
    if (!settled) {
      timer = setTimer(() => settle({
        ok: false,
        generation: request.generation,
        jobId: request.jobId,
        code: 'timeout',
      }), SOCIAL_RESIZE_WORKER_TIMEOUT_MS)
    }
  } catch {
    settle({
      ok: false,
      generation: request.generation,
      jobId: request.jobId,
      code: 'canvas_failed',
    })
  }
  return {
    promise,
    cancel: () => settle({
      ok: false,
      generation: request.generation,
      jobId: request.jobId,
      code: 'invalid_request',
    }),
  }
}
